import React from 'react';

import BulkPermissionUpdateSheet from '@/components/blocks/sheets/bulk-permission-update';
import CustomerCreateSheet from '@/components/blocks/sheets/customer-create';
import CustomerUpdateSheet from '@/components/blocks/sheets/customer-update';
import InventoryCreateSheet from '@/components/blocks/sheets/inventory-create';
import InventoryUpdateSheet from '@/components/blocks/sheets/inventory-update';
import OrderCreateSheet from '@/components/blocks/sheets/order-create';
import OrderUpdateSheet from '@/components/blocks/sheets/order-update';
import PurchaseOrderCreateSheet from '@/components/blocks/sheets/purchase-order-create';
import PurchaseOrderNoteCreateSheet from '@/components/blocks/sheets/purchase-order-note-create';
import PurchaseOrderNoteUpdateSheet from '@/components/blocks/sheets/purchase-order-note-update';
import PurchaseOrderUpdateSheet from '@/components/blocks/sheets/purchase-order-update';
import RequisitionCreateSheet from '@/components/blocks/sheets/requisition-create';
import RoleCreateSheet from '@/components/blocks/sheets/role-create';
import RoleUpdateSheet from '@/components/blocks/sheets/role-update';
import SendNotificationSheet from '@/components/blocks/sheets/send-notification';
import SourcingOfficerCreateSheet from '@/components/blocks/sheets/sourcing-officer-create';
import UserCreateSheet from '@/components/blocks/sheets/user-create';
import UserPermissionViewerSheet from '@/components/blocks/sheets/user-permission-viewer';
import UserUpdateSheet from '@/components/blocks/sheets/user-update';
import { SheetButton } from '@/contexts/GlobalStateContext';

// Sheet registry for different sheet types
export const sheetRegistry: Record<string, React.ComponentType<any>> = {
  'customer-create': CustomerCreateSheet,
  'customer-edit': CustomerCreateSheet, // Can be extended later for edit functionality
  'customer-update': CustomerUpdateSheet,
  'order-create': OrderCreateSheet,
  'create-order': OrderCreateSheet, // Alias for order-create
  'order-update': OrderUpdateSheet,
  'purchase-order-create': PurchaseOrderCreateSheet,
  'create-purchase-order': PurchaseOrderCreateSheet, // Alias for purchase-order-create
  'purchase-order-update': PurchaseOrderUpdateSheet,
  'edit-purchase-order': PurchaseOrderUpdateSheet, // Alias for purchase-order-update
  'purchase-order-note-create': PurchaseOrderNoteCreateSheet,
  'create-purchase-order-note': PurchaseOrderNoteCreateSheet, // Alias for purchase-order-note-create
  'purchase-order-note-update': PurchaseOrderNoteUpdateSheet,
  'edit-purchase-order-note': PurchaseOrderNoteUpdateSheet, // Alias for purchase-order-note-update
  'requisition-create': RequisitionCreateSheet,
  'create-requisition': RequisitionCreateSheet, // Alias for requisition-create
  'user-create': UserCreateSheet,
  'add-user': UserCreateSheet, // Alias for user-create
  'user-update': UserUpdateSheet,
  'edit-user': UserUpdateSheet, // Alias for user-update
  'role-create': RoleCreateSheet,
  'create-role': RoleCreateSheet, // Alias for role-create
  'role-update': RoleUpdateSheet,
  'send-notification': SendNotificationSheet,
  'sourcing-officer-create': SourcingOfficerCreateSheet,
  'add-sourcing-officer': SourcingOfficerCreateSheet, // Alias for sourcing-officer-create
  'bulk-permission-update': BulkPermissionUpdateSheet,
  'user-permission-viewer': UserPermissionViewerSheet,
  // Inventory
  'inventory-update': InventoryUpdateSheet,
  'edit-inventory': InventoryUpdateSheet, // Alias
  'edit-product': InventoryUpdateSheet, // Alias used by legacy code
  'inventory-create': InventoryCreateSheet,
  'create-inventory': InventoryCreateSheet,
  'create-product': InventoryCreateSheet,
};

// Sheet button configurations
export const getSheetButtons = (type: string): SheetButton[] => {
  switch (type) {
    case 'customer-create':
      return [
        {
          id: 'customer-action',
          label: 'Next',
          onClick: () => {
            const handlers = (window as any).customerCreateHandlers;
            if (handlers) {
              handlers.handleNextStep();
            }
          },
          color: 'brand',
          hierarchy: 'primary',
          loading: false,
          disabled: false,
          type: 'button',
        },
        {
          id: 'cancel-customer',
          label: 'Cancel',
          onClick: () => {
            const handlers = (window as any).customerCreateHandlers;
            if (handlers) {
              handlers.handleCancel();
            }
          },
          color: 'gray',
          hierarchy: 'secondary',
          type: 'button',
        },
      ];

    case 'purchase-order-create':
    case 'create-purchase-order': {
      const poHandlers = (window as any).purchaseOrderCreateHandlers;
      const currentStep = poHandlers?.currentStep || 1;
      const totalSteps = poHandlers?.totalSteps || 2;
      const isLoading = poHandlers?.isLoading || false;

      const buttons: SheetButton[] = [];

      // Back button (show on step 2)
      if (currentStep > 1) {
        buttons.push({
          id: 'back-purchase-order',
          label: 'Previous',
          onClick: () => {
            if (poHandlers) {
              poHandlers.handlePrevStep();
            }
          },
          color: 'gray',
          hierarchy: 'secondary',
          type: 'button',
        });
      }

      // Next/Create button
      if (currentStep < totalSteps) {
        buttons.push({
          id: 'purchase-order-action',
          label: 'Next',
          onClick: () => {
            if (poHandlers) {
              poHandlers.handleNextStep();
            }
          },
          color: 'brand',
          hierarchy: 'primary',
          loading: isLoading,
          disabled: isLoading,
          type: 'button',
        });
      } else {
        buttons.push({
          id: 'purchase-order-action',
          label: 'Create Purchase Order',
          onClick: () => {
            if (poHandlers) {
              poHandlers.handleSubmit();
            }
          },
          color: 'brand',
          hierarchy: 'primary',
          loading: isLoading,
          disabled: isLoading,
          type: 'button',
        });
      }

      // Cancel button
      buttons.push({
        id: 'cancel-purchase-order',
        label: 'Cancel',
        onClick: () => {
          if (poHandlers) {
            poHandlers.handleCancel();
          }
        },
        color: 'gray',
        hierarchy: 'secondary',
        type: 'button',
      });

      return buttons;
    }

    case 'purchase-order-update':
    case 'edit-purchase-order':
      return [
        {
          id: 'update-purchase-order',
          label: 'Update Purchase Order',
          onClick: () => {
            const handlers = (window as any).purchaseOrderUpdateHandlers;
            if (handlers) {
              handlers.handleSubmit();
            }
          },
          color: 'brand',
          hierarchy: 'primary',
          loading: false,
          disabled: false,
          type: 'button',
        },
        {
          id: 'cancel-purchase-order-update',
          label: 'Cancel',
          onClick: () => {
            const handlers = (window as any).purchaseOrderUpdateHandlers;
            if (handlers) {
              handlers.handleCancel();
            }
          },
          color: 'gray',
          hierarchy: 'secondary',
          type: 'button',
        },
      ];

    case 'purchase-order-note-create':
    case 'create-purchase-order-note': {
      const ponHandlers = (window as any).purchaseOrderNoteCreateHandlers;
      const currentStep = ponHandlers?.currentStep || 1;
      const totalSteps = ponHandlers?.totalSteps || 2;
      const isLoading = ponHandlers?.isLoading || false;

      const buttons: SheetButton[] = [];

      // Back button (show on step 2)
      if (currentStep > 1) {
        buttons.push({
          id: 'back-purchase-order-note',
          label: 'Back to Order Note Details',
          onClick: () => {
            if (ponHandlers) {
              ponHandlers.handlePrevStep();
            }
          },
          color: 'gray',
          hierarchy: 'secondary',
          type: 'button',
        });
      }

      // Next/Create button
      if (currentStep < totalSteps) {
        buttons.push({
          id: 'purchase-order-note-action',
          label: 'Proceed to Summary',
          onClick: () => {
            if (ponHandlers) {
              ponHandlers.handleNextStep();
            }
          },
          color: 'brand',
          hierarchy: 'primary',
          loading: isLoading,
          disabled: isLoading,
          type: 'button',
        });
      } else {
        buttons.push({
          id: 'purchase-order-note-action',
          label: 'Submit for Approval',
          onClick: () => {
            if (ponHandlers) {
              ponHandlers.handleSubmit();
            }
          },
          color: 'brand',
          hierarchy: 'primary',
          loading: isLoading,
          disabled: isLoading,
          type: 'button',
        });
      }

      // Cancel button
      buttons.push({
        id: 'cancel-purchase-order-note',
        label: 'Cancel',
        onClick: () => {
          if (ponHandlers) {
            ponHandlers.handleCancel();
          }
        },
        color: 'gray',
        hierarchy: 'secondary',
        type: 'button',
      });

      return buttons;
    }

    case 'purchase-order-note-update':
    case 'edit-purchase-order-note':
      return [
        {
          id: 'update-purchase-order-note',
          label: 'Update Purchase Order Note',
          onClick: () => {
            const handlers = (window as any).purchaseOrderNoteUpdateHandlers;
            if (handlers) {
              handlers.handleSubmit();
            }
          },
          color: 'brand',
          hierarchy: 'primary',
          loading: false,
          disabled: false,
          type: 'button',
        },
        {
          id: 'cancel-purchase-order-note-update',
          label: 'Cancel',
          onClick: () => {
            const handlers = (window as any).purchaseOrderNoteUpdateHandlers;
            if (handlers) {
              handlers.handleCancel();
            }
          },
          color: 'gray',
          hierarchy: 'secondary',
          type: 'button',
        },
      ];

    case 'order-create':
    case 'create-order': {
      const orderHandlers = (window as any).orderCreateHandlers;
      const currentStep = orderHandlers?.currentStep || 1;
      const totalSteps = orderHandlers?.totalSteps || 4;
      const isLoading = orderHandlers?.isLoading || false;

      const buttons: SheetButton[] = [];

      // Back button (show on steps 2, 3, 4)
      if (currentStep > 1) {
        buttons.push({
          id: 'back-order',
          label: 'Back',
          onClick: () => {
            if (orderHandlers) {
              orderHandlers.handlePrevStep();
            }
          },
          color: 'gray',
          hierarchy: 'secondary',
          type: 'button',
        });
      }

      // Next/Create button
      if (currentStep < totalSteps) {
        buttons.push({
          id: 'order-action',
          label: 'Next',
          onClick: () => {
            if (orderHandlers) {
              orderHandlers.handleNextStep();
            }
          },
          color: 'brand',
          hierarchy: 'primary',
          loading: isLoading,
          disabled: isLoading,
          type: 'button',
        });
      } else {
        buttons.push({
          id: 'order-action',
          label: 'Create Order',
          onClick: () => {
            if (orderHandlers) {
              orderHandlers.handleNextStep();
            }
          },
          color: 'brand',
          hierarchy: 'primary',
          loading: isLoading,
          disabled: isLoading,
          type: 'button',
        });
      }

      // Cancel button
      buttons.push({
        id: 'cancel-order',
        label: 'Cancel',
        onClick: () => {
          if (orderHandlers) {
            orderHandlers.handleCancel();
          }
        },
        color: 'gray',
        hierarchy: 'secondary',
        type: 'button',
      });

      return buttons;
    }

    case 'order-update':
      return [
        {
          id: 'update-order',
          label: 'Update Order',
          onClick: () => {
            const handlers = (window as any).orderUpdateHandlers;
            if (handlers) {
              handlers.handleSubmit();
            }
          },
          color: 'brand',
          hierarchy: 'primary',
          loading: false,
          disabled: false,
          type: 'button',
        },
        {
          id: 'cancel-order-update',
          label: 'Cancel',
          onClick: () => {
            const handlers = (window as any).orderUpdateHandlers;
            if (handlers) {
              handlers.handleCancel();
            }
          },
          color: 'gray',
          hierarchy: 'secondary',
          type: 'button',
        },
      ];

    case 'user-create':
    case 'add-user':
      return [
        {
          id: 'create-user',
          label: 'Create User',
          onClick: () => {
            const handlers = (window as any).userCreateHandlers;
            if (handlers) {
              handlers.handleCreateUser();
            }
          },
          color: 'brand',
          hierarchy: 'primary',
          loading: false,
          disabled: false,
          type: 'button',
        },
        {
          id: 'cancel-user',
          label: 'Cancel',
          onClick: () => {
            const handlers = (window as any).userCreateHandlers;
            if (handlers) {
              handlers.handleCancel();
            }
          },
          color: 'gray',
          hierarchy: 'secondary',
          type: 'button',
        },
      ];

    case 'user-update':
    case 'edit-user':
      return [
        {
          id: 'update-user',
          label: 'Update User',
          onClick: () => {
            const handlers = (window as any).userUpdateHandlers;
            if (handlers) {
              handlers.handleUpdateUser();
            }
          },
          color: 'brand',
          hierarchy: 'primary',
          loading: false,
          disabled: false,
          type: 'button',
        },
        {
          id: 'delete-user',
          label: 'Delete User',
          onClick: () => {
            const handlers = (window as any).userUpdateHandlers;
            if (handlers) {
              handlers.showDeleteConfirmation();
            }
          },
          color: 'destructive',
          hierarchy: 'secondary',
          type: 'button',
        },
        {
          id: 'cancel-user-update',
          label: 'Cancel',
          onClick: () => {
            const handlers = (window as any).userUpdateHandlers;
            if (handlers) {
              handlers.handleCancel();
            }
          },
          color: 'gray',
          hierarchy: 'secondary',
          type: 'button',
        },
      ];

    case 'role-create':
    case 'create-role':
      return [
        {
          id: 'create-role',
          label: 'Create Role',
          onClick: () => {
            const handlers = (window as any).roleCreateHandlers;
            if (handlers) {
              handlers.handleCreateRole();
            }
          },
          color: 'brand',
          hierarchy: 'primary',
          loading: false,
          disabled: false,
          type: 'button',
        },
        {
          id: 'cancel-role',
          label: 'Cancel',
          onClick: () => {
            const handlers = (window as any).roleCreateHandlers;
            if (handlers) {
              handlers.handleCancel();
            }
          },
          color: 'gray',
          hierarchy: 'secondary',
          type: 'button',
        },
      ];

    case 'role-update':
      return [
        {
          id: 'update-role',
          label: 'Update Role',
          onClick: () => {
            const handlers = (window as any).roleUpdateHandlers;
            if (handlers) {
              handlers.handleUpdateRole();
            }
          },
          color: 'brand',
          hierarchy: 'primary',
          loading: false,
          disabled: false,
          type: 'button',
        },
        {
          id: 'delete-role',
          label: 'Delete Role',
          onClick: () => {
            const handlers = (window as any).roleUpdateHandlers;
            if (handlers) {
              handlers.showDeleteConfirmation();
            }
          },
          color: 'destructive',
          hierarchy: 'secondary',
          type: 'button',
        },
        {
          id: 'cancel-role-update',
          label: 'Cancel',
          onClick: () => {
            const handlers = (window as any).roleUpdateHandlers;
            if (handlers) {
              handlers.handleCancel();
            }
          },
          color: 'gray',
          hierarchy: 'secondary',
          type: 'button',
        },
      ];

    case 'bulk-permission-update':
      return [
        {
          id: 'bulk-update',
          label: 'Update Users',
          onClick: () => {
            const handlers = (window as any).bulkPermissionUpdateHandlers;
            if (handlers) {
              handlers.handleBulkUpdate();
            }
          },
          color: 'brand',
          hierarchy: 'primary',
          loading: false,
          disabled: false,
          type: 'button',
        },
        {
          id: 'cancel-bulk-update',
          label: 'Cancel',
          onClick: () => {
            const handlers = (window as any).bulkPermissionUpdateHandlers;
            if (handlers) {
              handlers.handleCancel();
            }
          },
          color: 'gray',
          hierarchy: 'secondary',
          type: 'button',
        },
      ];

    case 'send-notification':
      return [
        {
          id: 'send-notification',
          label: 'Send Notification',
          onClick: () => {
            const handlers = (window as any).sendNotificationHandlers;
            if (handlers) {
              handlers.handleSendNotification();
            }
          },
          color: 'brand',
          hierarchy: 'primary',
          loading: false,
          disabled: false,
          type: 'button',
        },
        {
          id: 'cancel-notification',
          label: 'Cancel',
          onClick: () => {
            const handlers = (window as any).sendNotificationHandlers;
            if (handlers) {
              handlers.handleCancel();
            }
          },
          color: 'gray',
          hierarchy: 'secondary',
          type: 'button',
        },
      ];

    case 'create-requisition':
    case 'requisition-create': {
      const requisitionHandlers = (window as any).requisitionCreateHandlers;
      const currentStep = requisitionHandlers?.currentStep || 1;
      const totalSteps = requisitionHandlers?.totalSteps || 2;
      const isLoading = requisitionHandlers?.isLoading || false;

      const buttons: SheetButton[] = [];

      // Back button (show on step 2)
      if (currentStep > 1) {
        buttons.push({
          id: 'back-requisition',
          label: 'Back',
          onClick: () => {
            if (requisitionHandlers) {
              requisitionHandlers.handlePrevStep();
            }
          },
          color: 'gray',
          hierarchy: 'secondary',
          type: 'button',
        });
      }

      // Next/Create button
      if (currentStep < totalSteps) {
        buttons.push({
          id: 'requisition-action',
          label: 'Next',
          onClick: () => {
            if (requisitionHandlers) {
              requisitionHandlers.handleNextStep();
            }
          },
          color: 'brand',
          hierarchy: 'primary',
          loading: isLoading,
          disabled: isLoading,
          type: 'button',
        });
      } else {
        buttons.push({
          id: 'requisition-action',
          label: 'Create Requisition',
          onClick: () => {
            if (requisitionHandlers) {
              requisitionHandlers.handleNextStep();
            }
          },
          color: 'brand',
          hierarchy: 'primary',
          loading: isLoading,
          disabled: isLoading,
          type: 'button',
        });
      }

      // Cancel button
      buttons.push({
        id: 'cancel-requisition',
        label: 'Cancel',
        onClick: () => {
          if (requisitionHandlers) {
            requisitionHandlers.handleCancel();
          }
        },
        color: 'gray',
        hierarchy: 'secondary',
        type: 'button',
      });

      return buttons;
    }

    case 'edit-requisition':
    case 'requisition-update':
      return [
        {
          id: 'update-requisition',
          label: 'Update Requisition',
          onClick: () => {
            const handlers = (window as any).requisitionUpdateHandlers;
            if (handlers) {
              handlers.handleSubmit();
            }
          },
          color: 'brand',
          hierarchy: 'primary',
          loading: false,
          disabled: false,
          type: 'button',
        },
        {
          id: 'cancel-requisition-update',
          label: 'Cancel',
          onClick: () => {
            const handlers = (window as any).requisitionUpdateHandlers;
            if (handlers) {
              handlers.handleCancel();
            }
          },
          color: 'gray',
          hierarchy: 'secondary',
          type: 'button',
        },
      ];

    case 'create-supplier':
    case 'supplier-create': {
      const supplierHandlers = (window as any).supplierCreateHandlers;
      const currentStep = supplierHandlers?.currentStep || 1;
      const isLoading = supplierHandlers?.isLoading || false;

      const buttons: SheetButton[] = [];

      // Back button (show on steps 2 and 3)
      if (currentStep > 1) {
        const backLabels = {
          2: 'Back to Supplier Details',
          3: 'Back to KYC',
        };

        buttons.push({
          id: 'back-supplier',
          label: backLabels[currentStep as keyof typeof backLabels] || 'Back',
          onClick: () => {
            if (supplierHandlers) {
              supplierHandlers.handlePrevStep();
            }
          },
          color: 'gray',
          hierarchy: 'outlined',
          type: 'button',
        });
      }

      // Cancel button
      buttons.push({
        id: 'cancel-supplier',
        label: 'Cancel',
        onClick: () => {
          if (supplierHandlers) {
            supplierHandlers.handleCancel();
          }
        },
        color: 'gray',
        hierarchy: 'outlined',
        type: 'button',
      });

      // Primary action button (Next/Submit)
      const actionLabels = {
        1: 'Proceed to KYC',
        2: 'Proceed to Products',
        3: 'Submit for Approval',
      };

      buttons.push({
        id: 'supplier-action',
        label: actionLabels[currentStep as keyof typeof actionLabels] || 'Next',
        onClick: () => {
          if (supplierHandlers) {
            supplierHandlers.handleNextStep();
          }
        },
        color: 'brand',
        hierarchy: 'primary',
        loading: isLoading,
        disabled: isLoading,
        type: 'button',
      });

      return buttons;
    }

    case 'edit-supplier':
    case 'supplier-update':
      return [
        {
          id: 'update-supplier',
          label: 'Update Supplier',
          onClick: () => {
            const handlers = (window as any).supplierUpdateHandlers;
            if (handlers) {
              handlers.handleSubmit();
            }
          },
          color: 'brand',
          hierarchy: 'primary',
          loading: false,
          disabled: false,
          type: 'button',
        },
        {
          id: 'cancel-supplier-update',
          label: 'Cancel',
          onClick: () => {
            const handlers = (window as any).supplierUpdateHandlers;
            if (handlers) {
              handlers.handleCancel();
            }
          },
          color: 'gray',
          hierarchy: 'secondary',
          type: 'button',
        },
      ];

    default:
      return [];
  }
};

// Helper function to render sheet content
export const renderSheetContent = (type: string, data?: any) => {
  const SheetComponent = sheetRegistry[type];

  if (!SheetComponent) {
    return <div>Unknown sheet type: {type}</div>;
  }

  return <SheetComponent {...data} />;
};
